import {
  AfterViewInit,
  Component,
  ContentChild,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, fromEvent } from 'rxjs';
import { ACTIVE_CLASS_NAME } from '../../constants/constants';

@Component({
  selector: 'ui2-card-slideshow',
  templateUrl: './ui2-card-slideshow.component.html',
  styleUrls: ['./ui2-card-slideshow.component.scss']
})
export class Ui2CardSlideshowComponent implements AfterViewInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  /**
   * Receives the card list template from parent component.
   */
  @ContentChild('cardList')
  public cardListTemplate: TemplateRef<unknown>;

  /**
   * Receives the title to show for the slider.
   */
  @Input()
  public title: string;

  /**
   * Receives the card's count.
   *
   * Note: Used to toggle the next button
   * visibility in first load.
   */
  @Input()
  public cardCount: number = 0;

  /**
   * Receives the card's width.
   *
   * Note: Used to toggle the next button
   * visibility in first load.
   */
  @Input()
  public cardWidth: number = 0;

  /**
   * Receives the next button state from parent.
   * So that parent can override the behavior if want.
   */
  @Input()
  public isNextButtonActive: boolean = false;

  /**
   * Receives the next button state from parent.
   * So that parent can override the behavior if want.
   */
  @Input()
  public isPreviousButtonActive: boolean = false;

  /**
   * Card's Wrapper template reference.
   */
  @ViewChild('cardScrollWrapper')
  public cardScrollWrapper: ElementRef<HTMLDivElement>;

  // Class name
  public readonly activeClassName: string = ACTIVE_CLASS_NAME;

  // Event type
  private readonly scrollEventName: string = 'scroll';

  // Event type
  private readonly resizeEventName: string = 'resize';

  /**
   * Listen to input changes.
   *
   * @param changes - Simple change object.
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.cardCount?.currentValue) {
      /**
       * On First load calculate the scrollable area and card's
       * count * cards width so that we get to know whether to enable
       * next btn or not.
       */
      this.calculateAndToggleNextBtnVisibility();
    }
  }

  /**
   * After the view in initialized attach the listeners.
   */
  public ngAfterViewInit(): void {
    this.listenScroll();
    this.listenResize();
  }

  /**
   * Scroll the div to right
   */
  public scrollRight(): void {
    // Return if not active
    if (!this.isNextButtonActive) {
      return;
    }

    const bounds: DOMRect = this.cardScrollWrapper.nativeElement.getBoundingClientRect();
    this.cardScrollWrapper.nativeElement.scrollTo({
      left: this.cardScrollWrapper.nativeElement.scrollLeft + bounds.left
    });
  }

  /**
   * Scroll the div to left.
   */
  public scrollLeft(): void {
    // Return if not active
    if (!this.isPreviousButtonActive) {
      return;
    }

    const bounds: DOMRect = this.cardScrollWrapper.nativeElement.getBoundingClientRect();
    this.cardScrollWrapper.nativeElement.scrollTo({
      left: this.cardScrollWrapper.nativeElement.scrollLeft - bounds.left
    });
  }

  /**
   * Listen to scroll event in the scrollable div
   * and toggle the state of btn respectively.
   */
  private listenScroll(): void {
    fromEvent(this.cardScrollWrapper.nativeElement, this.scrollEventName)
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateButtonState();
      });
  }

  /**
   * Listen to the window resize event so that
   * we can update the controls button state.
   */
  private listenResize(): void {
    fromEvent(window, this.resizeEventName)
      .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateButtonState();
      });
  }

  /**
   * Update the button state
   */
  private updateButtonState(): void {
    this.togglePreviousBtnState();
    this.toggleNextBtnState();
  }

  /**
   * Check and disable the previous button.
   */
  private togglePreviousBtnState(): void {
    const left: number = this.cardScrollWrapper.nativeElement.scrollLeft;

    /**
     * If left is 0 that means user has not scrolled
     * so disable the btn else enable.
     */
    if (left === 0) {
      this.isPreviousButtonActive = false;
    } else {
      this.isPreviousButtonActive = true;
    }
  }

  /**
   * Check and disable the next button.
   */
  private toggleNextBtnState(): void {
    const bounds: DOMRect = this.cardScrollWrapper.nativeElement.getBoundingClientRect();
    const left: number = this.cardScrollWrapper.nativeElement.scrollLeft;
    const scrollWidth: number = this.cardScrollWrapper.nativeElement.scrollWidth;

    /**
     * If current left  + the width of the container
     * is greater or equals to the available scrollable width then disable
     * the next button.
     */
    if (Math.ceil(left + bounds.width) >= scrollWidth) {
      this.isNextButtonActive = false;
    } else {
      this.isNextButtonActive = true;
    }
  }

  /**
   * Calculate the count * width to get scrollable content
   * value.
   *
   * Note: Used to calculate and decide whether we enable the
   * next button on first load or not.
   */
  private calculateAndToggleNextBtnVisibility(): void {
    // Width of the scrollable content.
    const scrollWidth: number = this.cardScrollWrapper.nativeElement.scrollWidth;

    // Calculate and toggle visibility.
    this.isNextButtonActive = this.cardCount * this.cardWidth > scrollWidth;
  }
}
